// FilePicker — the single picker component for the entire app. Owns its
// dropzone hook, accept config, dashed border, apple-style vertical
// brand-tinted gradient, drag/reject states, and standard content
// (skeleton card stack + title + hint). Two shapes:
//
//   • Default — full picker tile. Sized via `size` (sm/md/lg). The render-prop
//     child replaces the standard content for advanced layouts (e.g. hero's
//     staged state). Callers who want the canonical idle inside a render-prop
//     can import <FilePickerIdle/>.
//   • Inline — small pill for "drop more" affordances inside lists or
//     sidebars. Single line of text, primary-tinted.
//
// Why one component: every picker funnels through here, so a styling
// change lands everywhere at once.
import { Plus } from "@phosphor-icons/react";
import { Upload } from "lucide-react";
import type { ReactNode } from "react";
import { type Accept, useDropzone } from "react-dropzone";
import { cn } from "@/lib/utils";

export type FilePickerSize = "sm" | "md" | "lg";

export type RejectReason = "too-large" | "wrong-type";

export interface FilePickerApi {
  /** Programmatically open the native file picker. */
  open: () => void;
  isDragActive: boolean;
  isDragReject: boolean;
}

interface CommonProps {
  accept: Accept;
  maxSize?: number;
  multiple?: boolean;
  onAddFiles: (files: File[]) => void;
  onReject?: (reason: RejectReason, file?: File) => void;
  /** Disable click-to-open. Drag-and-drop still works. */
  noClick?: boolean;
  className?: string;
}

interface DefaultVariantProps extends CommonProps {
  variant?: "default";
  size?: FilePickerSize;
  /**
   * Render-prop escape hatch. When provided, the picker renders the
   * function's output instead of the standard idle content — caller gets
   * the dropzone state plus an `open()` trigger for "add more" buttons.
   */
  children?: (api: FilePickerApi) => ReactNode;
}

interface InlineVariantProps extends CommonProps {
  variant: "inline";
  /** Pill body. Defaults to "Drop another file". */
  label?: ReactNode;
  icon?: ReactNode;
}

export type FilePickerProps = DefaultVariantProps | InlineVariantProps;

const SIZE_CHROME: Record<FilePickerSize, string> = {
  sm: "rounded-xl min-h-[220px]",
  md: "rounded-2xl min-h-[280px] sm:min-h-[300px]",
  lg: "rounded-3xl min-h-[300px] sm:min-h-[340px]",
};

const DEFAULT_MAX_SIZE = 25 * 1024 * 1024;

export function FilePicker(props: FilePickerProps) {
  const {
    accept,
    maxSize = DEFAULT_MAX_SIZE,
    multiple = true,
    onAddFiles,
    onReject,
    noClick,
    className,
  } = props;

  const { getRootProps, getInputProps, isDragActive, isDragReject, open } = useDropzone({
    accept,
    maxSize,
    multiple,
    noClick,
    // The page-level watcher (usePageDragWatcher) handles drops on whitespace.
    // react-dropzone's default preventDropOnDocument: true would preventDefault
    // those events first, swallowing the file silently.
    preventDropOnDocument: false,
    onDropAccepted: onAddFiles,
    onDropRejected: (rejections) => {
      if (!onReject) return;
      const r = rejections[0];
      const code = r?.errors[0]?.code;
      onReject(code === "file-too-large" ? "too-large" : "wrong-type", r?.file);
    },
  });

  if (props.variant === "inline") {
    return (
      <div
        {...getRootProps()}
        className={cn(
          // Flat tint for inline pills — gradients here read as visual noise
          // inside dense layouts. Border + background carry the affordance.
          "group relative cursor-pointer overflow-hidden rounded-lg border-[1.5px] border-dashed border-neutral-200 bg-primary/[0.03] px-3 py-2.5 text-center transition-colors duration-fast",
          !isDragActive && !isDragReject && "hover:border-primary/40 hover:bg-primary/[0.06]",
          isDragActive && "border-primary bg-primary/[0.10]",
          isDragReject && "border-destructive/50 bg-destructive/[0.06]",
          className,
        )}
      >
        <input {...getInputProps()} />
        <p className="inline-flex items-center justify-center gap-1.5 text-[11px] font-medium text-primary">
          {props.icon ?? <Plus weight="bold" className="size-3" />}
          {props.label ?? "Drop another file"}
        </p>
      </div>
    );
  }

  const size = props.size ?? "md";
  const interactive = !isDragActive && !isDragReject && !noClick;
  const customRender = props.children;

  return (
    <div
      {...getRootProps()}
      className={cn(
        // Apple-style vertical fade: a hint of brand at top, white through
        // the middle, a softer brand wash at the bottom — sets the tone
        // without ever competing with the content layered on top.
        "group relative isolate w-full overflow-hidden bg-white bg-gradient-to-b from-primary/[0.05] via-white to-primary/[0.02]",
        "border-2 border-dashed border-neutral-200 transition-colors duration-300",
        SIZE_CHROME[size],
        interactive &&
          "cursor-pointer hover:border-primary/40 hover:from-primary/[0.07] hover:to-primary/[0.04]",
        isDragActive && "border-primary from-primary/[0.10] to-primary/[0.06]",
        isDragReject && "border-destructive/50 from-destructive/[0.06] to-destructive/[0.02]",
        className,
      )}
    >
      <input {...getInputProps()} />
      {customRender ? (
        customRender({ open, isDragActive, isDragReject })
      ) : (
        <FilePickerIdle isDragActive={isDragActive} accept={accept} maxSize={maxSize} />
      )}
    </div>
  );
}

// Canonical idle content: fanned skeleton cards + "Drop your files here" +
// comma-separated extension hint. Exported so render-prop consumers (e.g. the
// hero) can show it when they're not in their staged state.
export function FilePickerIdle({
  isDragActive,
  accept,
  maxSize = DEFAULT_MAX_SIZE,
}: {
  isDragActive: boolean;
  accept: Accept;
  maxSize?: number;
}) {
  return (
    <div className="absolute inset-0 flex flex-col items-center justify-end gap-3 p-5 sm:gap-4 sm:p-6">
      <div className="relative min-h-0 w-full flex-1">
        <SkeletonStack isDragActive={isDragActive} />
      </div>

      <div className="flex flex-col items-center gap-1 text-center">
        <p className="flex items-center gap-2 text-base font-medium text-foreground">
          <Upload className="size-4 text-primary" aria-hidden />
          Drop your files here, or click to select
        </p>
        <p className="text-xs text-muted-foreground/80">{formatHint(accept, maxSize)}</p>
      </div>
    </div>
  );
}

function formatHint(accept: Accept, maxSize: number): ReactNode {
  const exts = new Set<string>();
  for (const list of Object.values(accept))
    for (const ext of list)
      exts.add(ext.startsWith(".") ? ext.toLowerCase() : `.${ext.toLowerCase()}`);
  const sizeMb = Math.round(maxSize / 1024 / 1024);
  const parts = [...exts];
  return (
    <>
      {parts.map((ext, i) => (
        <span key={ext}>
          <span className="font-light">{ext}</span>
          {i < parts.length - 1 ? ", " : ""}
        </span>
      ))}
      {" · "}max {sizeMb}MB per file
    </>
  );
}

function SkeletonStack({ isDragActive }: { isDragActive: boolean }) {
  // Three cards spread side-by-side, each pivoting from its own bottom-center.
  // The PDF sits forward; JPG/EML peek out to either side. On hover and on
  // drag-active they splay further — a small "I see you" gesture.
  return (
    <div className="relative flex size-full items-center justify-center">
      <div
        className={cn(
          "translate-x-1/2 -rotate-12",
          "transition-transform duration-500 ease-out",
          "group-hover:-rotate-[22deg]",
          isDragActive && "-rotate-[26deg]",
        )}
      >
        <SkeletonFile type="JPG" accent="bg-blue-50 text-blue-700" bars={3} />
      </div>

      <div
        className={cn(
          "z-10 animate-float-soft",
          "transition-transform duration-500 ease-out",
          "group-hover:-translate-y-1",
          isDragActive && "-translate-y-2 scale-[1.04]",
        )}
      >
        <SkeletonFile type="PDF" accent="bg-primary/10 text-primary" bars={4} />
      </div>

      <div
        className={cn(
          "-translate-x-1/2 rotate-12",
          "transition-transform duration-500 ease-out",
          "group-hover:rotate-[22deg]",
          isDragActive && "rotate-[26deg]",
        )}
      >
        <SkeletonFile type=".eml" accent="bg-purple-50 text-purple-700" bars={3} />
      </div>
    </div>
  );
}

function SkeletonFile({ type, accent, bars }: { type: string; accent: string; bars: number }) {
  return (
    <div className="relative h-36 w-28 overflow-hidden rounded-xl border border-neutral-200 bg-white shadow-[0_8px_24px_-12px_rgba(0,0,0,0.18)]">
      <div
        className="absolute right-0 top-0 size-5 bg-neutral-100"
        style={{ clipPath: "polygon(100% 0, 0 0, 100% 100%)" }}
        aria-hidden
      />

      <div className="flex flex-col gap-2 p-3">
        <span
          className={cn(
            "inline-block w-fit rounded-sm px-1.5 py-0.5 font-mono text-[9px] font-semibold uppercase tracking-wider",
            accent,
          )}
        >
          {type}
        </span>
        <div className="flex flex-col gap-1.5 pt-1">
          {Array.from({ length: bars }).map((_, i) => (
            <ShimmerBar
              key={`${type}-${i}`}
              widthClass={["w-full", "w-5/6", "w-4/5", "w-3/5"][i] ?? "w-2/3"}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function ShimmerBar({ widthClass }: { widthClass: string }) {
  return (
    <div
      className={cn(
        "h-1.5 animate-shimmer rounded-full bg-[length:200%_100%]",
        "bg-gradient-to-r from-neutral-100 via-neutral-200 to-neutral-100",
        widthClass,
      )}
    />
  );
}
